/**
 * Idempotency-Key minting (client side, no secrets involved).
 *
 * The contract examples are shaped `bind:nas:…:acs-device:…` / `act:…`: a
 * human-readable prefix plus the identity of the operation. The console reuses
 * one key per *intent* so a double click or a re-submit is a replay, not a
 * second command; a genuinely new intent gets a new key.
 *
 * This is NOT signing: signing happens server-side with the client's Ed25519
 * key, which never reaches the browser (plan §3, deferred W1 auth).
 */

function uuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Deterministic-free fallback for non-secure contexts (http:// LAN dev server).
  let out = '';
  for (let i = 0; i < 32; i += 1) {
    out += Math.floor(Math.random() * 16).toString(16);
  }
  return `${out.slice(0, 8)}-${out.slice(8, 12)}-4${out.slice(13, 16)}-a${out.slice(17, 20)}-${out.slice(20, 32)}`;
}

/** Keys are namespaced `ui:` so an operator can tell console traffic from BSS. */
export function actionIdempotencyKey(acsDeviceId: string, action: string): string {
  const short = acsDeviceId.replace(/^acs-device:/, '');
  return `ui:${action}:${short}:${uuid()}`;
}

export function bindIdempotencyKey(nasRef: string, acsDeviceId: string): string {
  return `ui:bind:${nasRef}:${acsDeviceId.replace(/^acs-device:/, '')}`;
}

/**
 * `POST /ops/v1/activations` requires the header (missing → 422), so the key is
 * part of the request, not an optimisation. Namespaced `ui:activate:` + the
 * identity of the intent (serial on OLT) + a nonce, so a double click replays
 * and a genuinely new activation attempt gets a new key.
 */
export function activationIdempotencyKey(serial: string, oltId: string): string {
  const clean = serial.trim() || 'new';
  const olt = oltId.trim() || 'olt';
  return `ui:activate:${clean}:${olt}:${uuid()}`;
}

/** One key per probe intent: type + device + target, so a replay re-reads, never re-probes. */
export function diagnosticsIdempotencyKey(
  acsDeviceId: string,
  type: string,
  target: string,
): string {
  const short = acsDeviceId.replace(/^acs-device:/, '') || 'device';
  const host = target.trim().replace(/[^A-Za-z0-9._:-]/g, '_') || 'target';
  return `ui:diag:${type}:${short}:${host}:${uuid()}`;
}

/**
 * FNV-1a over the identity of an intent, hex — stable across reloads of the same
 * session and across retries. This is a *label*, not a digest with any security
 * meaning: it exists so a key names the operation it belongs to and a ticket can
 * quote it.
 */
export function stableDigest(value: string): string {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h = Math.imul(h ^ value.charCodeAt(i), 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * `POST /ops/v1/alarms/{event_id}:ack`.
 *
 * The key is derived from the **alarm id alone** — no nonce, no clock — so
 * re-acking the same alarm can never mint a second key, which is exactly what an
 * ack is: one audit entry per (alarm, client). The server does not require the
 * header here (its dedupe is the `alarm.ack` ledger, see the `ackAlarm` summary
 * in `openapi/openapi.yaml`), and sending it costs nothing: the console policy is
 * one key per write intent, always.
 *
 * `alarm_id` is a CloudEvents id (`evt:…`), so it is folded to key-safe characters.
 */
export function alarmAckIdempotencyKey(alarmId: string): string {
  const clean = alarmId.trim().replace(/[^A-Za-z0-9._-]/g, '_');
  return `ui:ack:${clean || 'alarm'}:${stableDigest(alarmId)}`;
}

/**
 * The three lifecycle doors (N2): `:clear`, `:escalate` and `:assign`.
 *
 * The same rule as the ack — the key is derived from the alarm id alone, so a
 * double click cannot mint a second key — with the door's own prefix, so the
 * three intents of one alarm never share a key: closing, escalating and
 * assigning are three different writes, and a key that stood for all three
 * would replay one answer for another.
 */
export function alarmLifecycleIdempotencyKey(
  door: 'clear' | 'escalate' | 'assign',
  alarmId: string,
): string {
  const clean = alarmId.trim().replace(/[^A-Za-z0-9._-]/g, '_');
  return `ui:${door}:${clean || 'alarm'}:${stableDigest(alarmId)}`;
}

/**
 * `POST /ops/v1/olts/{olt_id}/onus:authorize` — a DIRECT OLT operation, so the
 * header is REQUIRED (missing → 422) and the key is part of the intent, not an
 * optimisation. One key per (OLT, sn, port) intent plus a nonce: a double click
 * replays the stored answer, while a deliberate new attempt is a new key — the
 * adapter authorize itself converges, so re-applying with a fresh key is the
 * documented repair path.
 */
export function onuAuthorizeIdempotencyKey(oltId: string, sn: string, ponPort: string): string {
  const olt = oltId.trim().replace(/[^A-Za-z0-9._-]/g, '_') || 'olt';
  const serial = sn.trim().replace(/[^A-Za-z0-9._-]/g, '_') || 'sn';
  const port = ponPort.trim().replace(/[^A-Za-z0-9._-]/g, '_') || 'port';
  return `ui:oltauth:${olt}:${serial}:${port}:${uuid()}`;
}

/** A file id / device id / campaign id, folded to characters a header value may carry. */
function keyPart(value: string, fallback: string): string {
  return value.trim().replace(/[^A-Za-z0-9._-]/g, '_') || fallback;
}

/**
 * `POST /ops/v1/firmware:upload` — the raw byte channel (E1 / G-FW-01).
 *
 * A nonce, and deliberately NOT a digest of the file: hashing the image here would mean
 * reading megabytes in the browser to produce a claim the façade is about to measure for
 * real (E2 / G-FW-02). One key per submission, with the control taken away while the write
 * is in flight, so a double click cannot become a second upload of the same bytes.
 */
export function firmwareUploadIdempotencyKey(
  brand: string,
  model: string,
  version: string,
): string {
  return `ui:fwup:${keyPart(brand, 'brand')}:${keyPart(model, 'model')}:${keyPart(version, 'version')}:${uuid()}`;
}

/**
 * `DELETE /ops/v1/firmware/{file_id}` — the contract requires the header because a replay
 * of this write is not equivalent (the second delete answers 404). A stored 404 served back
 * to a retry after a typo would be worse than the original failure, so the key names one
 * submission, not one file.
 */
export function firmwareDeleteIdempotencyKey(fileId: string): string {
  return `ui:fwdel:${keyPart(fileId, 'file')}:${uuid()}`;
}

/** `POST /ops/v1/campaigns` — one key per creation intent, aimed at one image. */
export function campaignCreateIdempotencyKey(fileId: string): string {
  return `ui:campnew:${keyPart(fileId, 'image')}:${uuid()}`;
}

/**
 * The three ladder verbs (`:halt`, `:resume`, `:rollback`).
 *
 * A nonce is the honest choice: halting a ladder AGAIN after a resume is a new intention, and
 * a key derived from the campaign alone would answer it with the stored reply of the first
 * halt — the operator would see «halted» while nothing had been sent. The button is disabled
 * while its write is in flight, which is what keeps a double click a replay instead.
 */
export function campaignHaltIdempotencyKey(campaignId: string): string {
  return `ui:camphalt:${keyPart(campaignId, 'campaign')}:${uuid()}`;
}

export function campaignResumeIdempotencyKey(campaignId: string): string {
  return `ui:campres:${keyPart(campaignId, 'campaign')}:${uuid()}`;
}

export function campaignRollbackIdempotencyKey(campaignId: string): string {
  return `ui:camprb:${keyPart(campaignId, 'campaign')}:${uuid()}`;
}

/**
 * `DELETE /ops/v1/sessions/current` — the logout write (R1, U-08-f).
 *
 * The header is REQUIRED here, and its reachable effect is the CONCURRENT case: an operator
 * double-clicking, or a client with two requests in flight, has both calls authenticated before
 * either revokes, and the second must be answered with the first's timestamp instead of racing
 * to revoke again. So the key names the session and nothing else — no nonce — which is also what
 * makes the door's other rule reachable: a key that was already used to revoke a DIFFERENT
 * session is a 409 `idempotency-conflict`, because the server's sketch carries the `sid`.
 */
export function sessionLogoutIdempotencyKey(sid: string): string {
  return `ui:logout:${keyPart(sid, 'session')}`;
}

/**
 * `PUT /ops/v1/operator-profile` — the header is REQUIRED (every success mints a version, so a
 * retry without a key is a second version nobody approved twice). A nonce, and one per submission
 * intent: a losing CAS (409 `profile-stale-version`) is not replayed automatically — the operator
 * re-reads, diffs and decides — so the next deliberate attempt is a new key by design.
 */
export function profileActivateIdempotencyKey(expectedVersion: number): string {
  return `ui:profileact:v${expectedVersion}:${uuid()}`;
}

/**
 * `POST /ops/v1/operator-profile:rollback` — required for the same reason as the activation door
 * (a rollback IS an activation). The key names the intent (which version comes forward, from which
 * version), so an accidental double-click replays the stored answer instead of minting two
 * forward versions.
 */
export function profileRollbackIdempotencyKey(toVersion: number, expectedVersion: number): string {
  return `ui:profilerb:to${toVersion}:at${expectedVersion}:${uuid()}`;
}

/**
 * `PUT /ops/v1/devices/{id}/operations/{op}` — the header is REQUIRED (missing → 422) and the door's
 * fan-out is a connection request plus an effect on a subscriber's board.
 *
 * One key per submission intent, with a nonce: a refusal under the gate records NOTHING server-side
 * («a 409 that also wrote a job would be a lie twice»), so the operator's next deliberate attempt has
 * to be a new key, while a double click while the request is in flight replays the stored 202 and the
 * same `job_id` back. The slot id is in the key because writing `wifi.ssid.primary` and writing
 * `wan.enable` are two intentions, not one.
 */
export function operationSetIdempotencyKey(acsDeviceId: string, op: string): string {
  return `ui:opset:${keyPart(acsDeviceId, 'device')}:${keyPart(op, 'op')}:${uuid()}`;
}

/**
 * `POST …/operations/{op}:run` — an RPC is the least replayable thing in this API (a second `Reboot`
 * is a second reboot), so the key names device + slot + a fresh nonce and the button is disabled while
 * the write is in flight. `device.factory_reset` opens this door through `ConfirmDialog`.
 */
export function operationRunIdempotencyKey(acsDeviceId: string, op: string): string {
  return `ui:oprun:${keyPart(acsDeviceId, 'device')}:${keyPart(op, 'op')}:${uuid()}`;
}

/**
 * `PUT …/parameters/raw` — the expert door, one job over N paths.
 *
 * The path COUNT is in the key on purpose: §۶.۱'s confirmation text is `WRITE-RAW <model> <count>`,
 * so a body whose count differs is a different request, and reusing one key for it would replay the
 * stored 202 of a write that covers other paths entirely.
 */
export function rawWriteIdempotencyKey(acsDeviceId: string, count: number): string {
  return `ui:raw:${keyPart(acsDeviceId, 'device')}:n${count}:${uuid()}`;
}

/**
 * `POST /ops/v1/users` — the header is REQUIRED by the door (`Header(...)`), so a missing key is a 422
 * before the store is touched.
 *
 * The username is in the key because it is what makes the request the same intent twice: a create of
 * `sahar` and a create of `sahur` are two accounts, and one key may not answer for both. The nonce is
 * what makes a SECOND deliberate create (after a refusal, after the operator fixed the name) a new key
 * rather than a replay of the stored 409.
 */
export function userCreateIdempotencyKey(username: string): string {
  return `ui:usercreate:${keyPart(username, 'new')}:${uuid()}`;
}

/**
 * `PATCH /ops/v1/users/{user_id}` — one key per (row, field) intent.
 *
 * The door's sketch carries `role`, `status` and the password FLAG separately, and the three fields are
 * independent on purpose (`routers/identity.py::patch_user`), so disabling a user and rotating their
 * password are two intentions with two keys. Reusing one key across both would replay the stored answer
 * of the first write onto the second — and on the wire it is a 409 `idempotency-conflict`, so minting
 * per field is the shape that keeps a double click a replay and a new decision a new request.
 */
export function userPatchIdempotencyKey(userId: string, field: string): string {
  return `ui:userpatch:${keyPart(userId, 'user')}:${keyPart(field, 'field')}:${uuid()}`;
}

/**
 * `DELETE /ops/v1/users/{user_id}` — required, and the row id is the whole intent.
 *
 * A delete is the least replayable write in this family (a second one is a 404 the operator did not ask
 * for), which is exactly why the key is not derived from a timestamp: the same key replayed returns the
 * stored answer, and the operator's next deliberate attempt after a refusal is a new key.
 */
export function userDeleteIdempotencyKey(userId: string): string {
  return `ui:userdelete:${keyPart(userId, 'user')}:${uuid()}`;
}

/**
 * `PUT /ops/v1/settings` — the key names WHICH keys this write touches, never their values.
 *
 * `system.py::write_settings` merges, so a body of `{periodic_inform_base_seconds}` and a body of
 * `{cr_rate_limit_per_sec}` are two different intentions even when typed seconds apart, and one key may
 * not answer for both. The values stay out of the key deliberately: the sketch the façade stores already
 * carries the body, so a client-side digest of it would be a second copy of a claim the server makes.
 */
export function opsSettingsIdempotencyKey(keys: readonly string[]): string {
  const part = [...keys]
    .sort()
    .map((key) => keyPart(key, 'k'))
    .join('+');
  return `ui:opsset:${part || 'empty'}:${uuid()}`;
}

/**
 * `PUT /ops/v1/permissions` — a change of law, so the key names WHICH CELLS this write touches.
 *
 * The door's stored sketch already carries the cells WITH their levels (`identity.py::write_permission_matrix`:
 * `sketch = {"endpoint": "permissions:put", "cells": cells}`), so a double click on the same draft replays
 * the stored answer and a same-key different-body retry is the door's 409 `idempotency-conflict` — the key
 * does not have to digest the values to make that true, and repeating them here would be a second copy of a
 * claim the server keeps. Like `opsSettingsIdempotencyKey`, the key names the intent's identity (the sorted
 * `capability:role` cells) and adds a nonce, so the operator's NEXT deliberate decision after a refusal is
 * a new request rather than a replay of the stored refusal.
 */
export function matrixWriteIdempotencyKey(
  cells: readonly { capability: string; role: string }[],
): string {
  const part = [...cells]
    .map((cell) => `${keyPart(cell.capability, 'cap')}:${keyPart(cell.role, 'role')}`)
    .sort()
    .join('+');
  return `ui:matrix:${part || 'cells'}:${uuid()}`;
}

/**
 * `POST /ops/v1/maintenance` (ALM-05). The key is namespaced per INTENT with
 * a nonce — the server keeps NO dedupe on window creation, so two genuine
 * submits really are two quiet nights and must not share a key. The scope and
 * the target ride in the key so an operator reading an idempotency ledger can
 * tell which night a write planned.
 */
export function maintenanceCreateIdempotencyKey(scope: string, target: string): string {
  const clean = target.trim().replace(/[^A-Za-z0-9._:-]/g, '_') || 'target';
  return `ui:window:${scope}:${clean}:${uuid()}`;
}

/**
 * `POST /ops/v1/maintenance/{window_id}:cancel` — derived from the window id
 * ALONE, no nonce (the alarm-lifecycle precedent): re-cancelling the same
 * window is the same intent, the server answers `already_cancelled: true`,
 * and no second key can exist for a plan that was already cancelled.
 */
export function maintenanceCancelIdempotencyKey(windowId: string): string {
  const clean = windowId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  return `ui:window:cancel:${clean || 'window'}`;
}

/* ---- W5b — the subscriber surface's three key-taking writes (2026-11-04) ----
   The doors are the EXISTING ones (S1/S4); this is only the console's key shape. The
   two creates mint a row, so a nonce is part of the intent (a second deliberate create
   must be a second row, never a replay). A lifecycle step is one field on one row and
   the door reserves the key BEFORE judging the row, so its key names the step and the
   subscriber and adds a nonce for the next deliberate decision. `PUT /service-profiles/{id}`
   takes NO key at all — the body IS the row — so it has no helper here on purpose. */

export function subscriberCreateIdempotencyKey(): string {
  return `ui:sub:create:${uuid()}`;
}

export function subscriberLifecycleIdempotencyKey(
  step: 'suspend' | 'resume',
  subscriberId: string,
): string {
  return `ui:sub:${step}:${keyPart(subscriberId, 'subscriber')}:${uuid()}`;
}

export function serviceProfileCreateIdempotencyKey(name: string): string {
  return `ui:sub:profile:${keyPart(name, 'new')}:${uuid()}`;
}

/**
 * `POST /ops/v1/incidents` (N5). The key is namespaced per INTENT with a
 * nonce, exactly like the maintenance create: the server DOES dedupe on the
 * key (a replay answers the first incident), but two genuine submits are two
 * incidents about (possibly) two outages and must not share a key. The title
 * rides in it so an idempotency ledger names the outage.
 */
export function incidentCreateIdempotencyKey(title: string): string {
  const clean =
    title
      .trim()
      .replace(/[^A-Za-z0-9._:-]/g, '_')
      .slice(0, 40) || 'incident';
  return `ui:incident:${clean}:${uuid()}`;
}

/**
 * `POST /ops/v1/incidents/{id}:note` — derived from the incident id and the
 * entry text: the same words on the same incident are the SAME note (a retry
 * must not write a second timeline entry), while different words are a new
 * intent. A nonce would break that equivalence, so — unlike the create — the
 * note's key carries no nonce.
 */
export function incidentNoteIdempotencyKey(incidentId: string, text: string): string {
  const cleanId = incidentId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  const cleanText = text
    .trim()
    .replace(/[^A-Za-z0-9._:-]/g, '_')
    .slice(0, 40);
  return `ui:incident:note:${cleanId || 'incident'}:${cleanText || 'note'}`;
}

/**
 * `POST /ops/v1/incidents/{id}:close` — derived from the incident id ALONE:
 * closing the same incident twice is the same intent, the server answers the
 * second one with 409 `incident-already-closed`, and a nonce would let a
 * retry pretend to be a new decision.
 */
export function incidentCloseIdempotencyKey(incidentId: string): string {
  const clean = incidentId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  return `ui:incident:close:${clean || 'incident'}`;
}

/**
 * `POST /ops/v1/notification-channels` (N6). The key is namespaced per INTENT
 * with a nonce, like the incident create: the server dedupes on the key (a
 * replay answers the first channel) but two genuine submits are two channels
 * and must not share a key. The name rides in it so an idempotency ledger
 * names the channel.
 */
export function channelCreateIdempotencyKey(name: string): string {
  const clean =
    name
      .trim()
      .replace(/[^A-Za-z0-9._:-]/g, '_')
      .slice(0, 40) || 'channel';
  return `ui:channel:${clean}:${uuid()}`;
}

/**
 * `PUT /ops/v1/notification-channels/{id}` — the FIELDS this write touches
 * ride in the key (the server's sketch carries the sorted change list), with a
 * nonce: disabling a channel and rotating its URL are two intentions, and one
 * key may not answer for both.
 */
export function channelUpdateIdempotencyKey(channelId: string, fields: readonly string[]): string {
  const clean = channelId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  const part = fields
    .map((field) => keyPart(field, 'f'))
    .sort()
    .join('+');
  return `ui:channel:upd:${clean || 'channel'}:${part || 'none'}:${uuid()}`;
}

/**
 * `DELETE /ops/v1/notification-channels/{id}` — derived from the id ALONE, no
 * nonce (the incident-close precedent): deleting the same channel twice is the
 * same intent, the replay answers the stored delete, and a nonce would let a
 * retry pretend the second delete is a new decision. (A genuinely new delete
 * after a 404 means the row was already gone — the door's own answer.)
 */
export function channelDeleteIdempotencyKey(channelId: string): string {
  const clean = channelId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  return `ui:channel:del:${clean || 'channel'}`;
}

/**
 * `POST …/{channel_id}:test` — the key IS the intent's dedupe key
 * (`{channel_id}|test|{key}` on the server), so a replay is the SAME row. A
 * nonce is what makes a SECOND deliberate test possible at all: a key derived
 * from the id alone would replay the first intent forever and the operator
 * could never fire a second one. One key per click, with the button disabled
 * while the write is in flight.
 */
export function channelTestIdempotencyKey(channelId: string): string {
  const clean = channelId.trim().replace(/[^A-Za-z0-9._:-]/g, '_');
  return `ui:chtest:${clean || 'channel'}:${uuid()}`;
}

/**
 * The template write's default key: the whole document travels in the server's idempotency
 * sketch (G-PRV-05), so the same key + a CHANGED document is a 409 replay conflict by design.
 * The edit form therefore offers «کلید جدید» (a fresh nonce) exactly like the device write
 * forms do — the default stays derived so a double-click is still one write.
 */
export function templateWriteIdempotencyKey(name: string): string {
  return `ui:template:${name}`;
}

/**
 * `POST /ops/v1/config:apply` — one key per CONFIRMED restore.
 *
 * The digest names which bundle the intent is about and a nonce separates two deliberate restores of
 * the same file (re-applying after the state moved is a new decision, not a replay). The key is minted
 * when the operator confirms, and the confirm dialog closes at once, so a double click cannot become
 * a second restore; a network retry of the SAME request carries the same key and replays.
 */
export function configApplyIdempotencyKey(digest: string): string {
  const short = digest.replace(/^sha256:/, '').slice(0, 16);
  return `ui:config:apply:${keyPart(short, 'bundle')}:${uuid()}`;
}

/* ---- W5a — the device-lifecycle writes (2026-11-04) ----
   `bind` reuses `bindIdempotencyKey` above: the intent is the same one that helper was
   written for (a NAS ref claiming a device), and a second name for it would be a second
   key shape for one door. The three below name an intent that is genuinely new, and each
   carries a nonce because a SECOND deliberate replace/decommission/discover of the same
   device is a new decision, not a replay. The two TAG doors have no helper on purpose:
   the contract declares no `Idempotency-Key` for them. */

export function deviceReplaceIdempotencyKey(acsDeviceId: string, newSerial: string): string {
  return `ui:dev:replace:${keyPart(acsDeviceId, 'device')}:${keyPart(newSerial, 'serial')}:${uuid()}`;
}

export function deviceDecommissionIdempotencyKey(acsDeviceId: string): string {
  return `ui:dev:decommission:${keyPart(acsDeviceId, 'device')}:${uuid()}`;
}

export function deviceDiscoverIdempotencyKey(acsDeviceId: string): string {
  return `ui:dev:discover:${keyPart(acsDeviceId, 'device')}:${uuid()}`;
}

/* ---- W5c — system maintenance (2026-11-04). Five key factories for five key-taking
   writes; the verify write takes NONE on purpose (the door refuses a cached verdict —
   see `docs/plans/2026-11-04-w5c-maintenance-console.md` MW-4). */

export function outboxRedriveIdempotencyKey(eventId: string): string {
  return `ui:ops:redrive:${keyPart(eventId, 'event')}:${uuid()}`;
}

export function migrationsApplyIdempotencyKey(): string {
  return `ui:ops:migrations-apply:${uuid()}`;
}

export function retentionRunIdempotencyKey(): string {
  return `ui:ops:retention-run:${uuid()}`;
}

export function legalHoldPlaceIdempotencyKey(scope: string): string {
  return `ui:ops:legal-hold:${keyPart(scope, 'scope')}:${uuid()}`;
}

export function legalHoldLiftIdempotencyKey(holdId: string): string {
  return `ui:ops:legal-hold-lift:${keyPart(holdId, 'hold')}:${uuid()}`;
}

export function userTotpEnrollIdempotencyKey(userId: string): string {
  return `ui:user:totp-enroll:${keyPart(userId, 'user')}:${uuid()}`;
}

export function userTotpResetIdempotencyKey(userId: string): string {
  return `ui:user:totp-reset:${keyPart(userId, 'user')}:${uuid()}`;
}

export function onuRebootIdempotencyKey(oltId: string, sn: string): string {
  return `ui:olt:reboot:${keyPart(oltId, 'olt')}:${keyPart(sn, 'sn')}:${uuid()}`;
}

export function onuBulkAuthorizeIdempotencyKey(oltId: string): string {
  return `ui:olt:bulk-authorize:${keyPart(oltId, 'olt')}:${uuid()}`;
}
